import { motion } from "framer-motion";
import Eyebrow from "./Eyebrow";

/**
 * Internal page hero with a wide rectangular image (16:7 ratio) — rounded corners.
 * Mobile: image stacks below; Desktop: image is at the right (or replaces background).
 */
export default function PageHero({ eyebrow, title, subtitle, image, imageAlt = "", children }) {
  return (
    <section className="relative bg-grid border-b border-border">
      <div className="container-page section-y">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Eyebrow className="mb-5">{eyebrow}</Eyebrow>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl"
        >
          {title}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl"
          >
            {subtitle}
          </motion.p>
        )}

        {children && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8"
          >
            {children}
          </motion.div>
        )}

        {/* Wide rectangular image, rounded corners */}
        {image && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.35, ease: "easeOut" }}
            className="mt-12 md:mt-16"
          >
            <div className="relative aspect-[16/7] sm:aspect-[16/6] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
              <img
                src={image}
                alt={imageAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* subtle overlay for cohesion */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent dark:from-dc-navy/60" />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
